import React, { useEffect, useMemo, useState } from "react";
import KPITiles from "@/components/KPITiles";
import StatsScopeToggle from "@/components/StatsScopeToggle";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import MapView from "@/components/MapView";
import { Route as RouteIcon, Lock, LockOpen } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import useOrderEvents from "@/hooks/useOrderEvents";
import { fmtTime } from "@/lib/datetime";
import { STATUS_HEX, STATUS_LABEL, personInitials, effectiveStatus, markerColor, UNASSIGNED_NEXT_DAY_HEX } from "@/lib/orderStatus";
import { vehicleColorHex } from "@/lib/vehicleColors";
import { useRegion } from "@/context/RegionContext";

const ROUTE_COLORS = ["#234123", "#3a86ff", "#8E44AD", "#F39C12", "#16A085", "#E91E63", "#34495E", "#FF6D00", "#00ACC1", "#7A9B7A"];

export default function SuperAdmin() {
  const { user } = useAuth();
  const routesHref = user?.role === "dispatcher" ? "/dispatch/routes" : "/admin/routes";
  const [stats, setStats] = useState({});
  const [routes, setRoutes] = useState([]);
  const [details, setDetails] = useState([]); // full route docs with stops + polyline
  const [pharmacies, setPharmacies] = useState([]);
  const [driverLocs, setDriverLocs] = useState({}); // driver_id -> {lat,lng,at}
  const [liveDrivers, setLiveDrivers] = useState([]); // all drivers with status
  // Freeze the live map (lock pan/zoom). Persisted to localStorage so a
  // super-admin who freezes it once doesn't need to re-freeze after refresh.
  const [mapFrozen, setMapFrozenRaw] = useState(() => {
    try { return localStorage.getItem("overviewMapFrozen") === "1"; } catch { return false; }
  });
  const setMapFrozen = (v) => {
    setMapFrozenRaw((prev) => {
      const next = typeof v === "function" ? v(prev) : v;
      try { localStorage.setItem("overviewMapFrozen", next ? "1" : "0"); } catch {}
      return next;
    });
  };
  const [unassigned, setUnassigned] = useState([]); // orders not on a route
  const [stopStatusById, setStopStatusById] = useState({}); // order_id -> current order status (so delivered stops can turn green live)
  const [needsReturn, setNeedsReturn] = useState({ count: 0, orders: [] }); // orders failed twice → must be returned
  const [scope, setScope] = useState("week"); // "month" | "week"
  // Overview-map deliver-type filter — super_admin only. Lets the admin
  // isolate same-day vs next-day stops across the fleet without changing
  // the underlying route plan. Values: "all" | "same_day" | "next_day".
  // Default "all" so first render matches the pre-2026-10-07 behaviour.
  const [mapTypeFilter, setMapTypeFilter] = useState("all");

  const load = async () => {
    const [s, r, p, ld, ua, all, nr] = await Promise.all([
      api.get("/stats/overview", { params: { scope } }),
      api.get("/routes"),
      api.get("/pharmacies"),
      api.get("/drivers/live").catch(() => ({ data: [] })),
      api.get("/orders", { params: { unassigned: true } }).catch(() => ({ data: [] })),
      // Fetch every order with a route_id so we can color delivered stops green
      // independently of the route-level status. Cheap (one extra request, joins
      // into the existing 12s poll).
      api.get("/orders").catch(() => ({ data: [] })),
      api.get("/admin/orders/needs-return").catch(() => ({ data: { count: 0, orders: [] } })),
    ]);
    setStats(s.data);
    setPharmacies(p.data);
    setLiveDrivers(ld.data || []);
    setUnassigned((ua.data || []).filter((o) => o.lat != null && o.lng != null));
    // Overview shows every OPEN route: planned (not started), in_progress
    // (rolling), AND paused (temporarily halted by the driver). Paused
    // routes must remain visible until the driver resumes them so
    // dispatch always knows where the driver is parked.
    const active = r.data.filter((x) =>
      x.status === "planned" || x.status === "in_progress" || x.status === "paused"
    );
    setRoutes(active);
    setDetails(active);
    setNeedsReturn(nr.data || { count: 0, orders: [] });
    // Build {order_id: status} for fast per-stop lookup on the map
    const sMap = {};
    (all.data || []).forEach((o) => { sMap[o.id] = o.status; });
    setStopStatusById(sMap);
    // Use the same /drivers/live data for the per-route driver marker
    const locs = {};
    (ld.data || []).forEach((d) => {
      if (d.lat != null && d.lng != null && d.status !== "offline") {
        locs[d.id] = { lat: d.lat, lng: d.lng, at: d.at };
      }
    });
    setDriverLocs(locs);
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 12000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  // Real-time push: recolor map circles + refresh KPI counts the moment a
  // driver marks an order Delivered / Failed. Falls back to the 12-second
  // poll if the SSE connection drops.
  useOrderEvents((update) => {
    setStopStatusById((prev) => ({ ...prev, [update.id]: update.status }));
    // Fire-and-forget stats refresh so KPI tiles reflect the new count.
    api.get("/stats/overview", { params: { scope } })
      .then((r) => setStats(r.data))
      .catch(() => {});
  });

  // Build a stitched map view: pharmacy markers (all), route polylines, driver markers
  const allStops = useMemo(() => {
    const list = [];
    details.forEach((r, idx) => {
      r.stops?.forEach((s) => list.push({ ...s, _routeIdx: idx }));
    });
    return list;
  }, [details]);

  const pharmacyStops = useMemo(
    () => pharmacies.filter((p) => p.lat != null && p.lng != null).map((p) => ({
      order_id: `ph-${p.id}`, lat: p.lat, lng: p.lng, customer_name: p.name, address: p.address, sequence: "", priority: "normal",
    })),
    [pharmacies]
  );

  // Apply the mapTypeFilter (super_admin only). When "all", pass through
  // unchanged. Otherwise:
  //   - route stops: keep pickup stops (no deliver_type) + stops whose
  //     deliver_type matches. We DON'T drop the whole route — the polyline
  //     + driver marker remain so admin can still see what the driver is
  //     doing, just with the non-matching stop pins hidden.
  //   - unassigned: filter by deliver_type match.
  const filteredDetails = useMemo(() => {
    if (mapTypeFilter === "all") return details;
    return details.map((r) => ({
      ...r,
      stops: (r.stops || []).filter((s) => {
        if (s.is_pickup) return true;
        const dt = (s.deliver_type || "").toLowerCase();
        return dt === mapTypeFilter;
      }),
    }));
  }, [details, mapTypeFilter]);

  const filteredUnassigned = useMemo(() => {
    if (mapTypeFilter === "all") return unassigned;
    return unassigned.filter((o) => (o.deliver_type || "").toLowerCase() === mapTypeFilter);
  }, [unassigned, mapTypeFilter]);

  return (
    <div className="p-6 lg:p-10 max-w-[1600px] mx-auto" data-testid="super-admin-page">
      <header className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl lg:text-4xl font-display font-bold tracking-tight">Overview</h1>
          <p className="text-muted-foreground mt-1.5">A snapshot of today, with the live map below.</p>
        </div>
        <StatsScopeToggle value={scope} onChange={setScope} />
      </header>

      <section data-testid="overview-kpis">
        {needsReturn.count > 0 && (
          <div
            className="mb-4 rounded-xl border-2 border-destructive/40 bg-destructive/5 px-4 py-3 flex items-start gap-3"
            data-testid="needs-return-alert"
          >
            <div className="text-destructive text-xl leading-none mt-0.5">⚠️</div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-destructive">
                {needsReturn.count} order{needsReturn.count === 1 ? "" : "s"} must be Returned to Pharmacy
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {needsReturn.count === 1 ? "This order has" : "These orders have"} failed delivery twice. {needsReturn.count === 1 ? "It" : "They"} can no longer be re-routed — please return to the pharmacy.
              </p>
              <ul className="mt-2 space-y-1">
                {needsReturn.orders.slice(0, 5).map((o) => (
                  <li key={o.id} className="text-xs font-mono-num flex flex-wrap items-center gap-x-2">
                    <span className="font-semibold">#{o.order_number || o.id.slice(0, 8)}</span>
                    <span>· {o.customer_name}</span>
                    {o.pharmacy_name && <span className="text-muted-foreground">· {o.pharmacy_name}</span>}
                    <Link to={`/admin/orders?focus=${o.id}`} className="text-destructive underline-offset-2 hover:underline ml-auto">
                      Open
                    </Link>
                  </li>
                ))}
                {needsReturn.orders.length > 5 && (
                  <li className="text-xs text-muted-foreground">…and {needsReturn.orders.length - 5} more</li>
                )}
              </ul>
            </div>
          </div>
        )}
        <KPITiles
          items={[
            { label: "Unassigned", value: stats.out_of_route, hint: "Out of route", color: "yellow", to: "/admin/orders?status=unassigned" },
            {
              label: "Accepted",
              value: stats.received_from_pharmacy,
              hint: stats.pending_intake ? `${stats.pending_intake} pending intake` : "from pharmacy",
              color: "lightBlue",
              to: "/admin/orders?status=accepted",
            },
            { label: "On Route", value: stats.routed, hint: "Orders on a route", color: "sky", to: "/admin/orders?status=on_route" },
            { label: "Delivered", value: stats.delivered, hint: "Completed", color: "green", to: "/admin/orders?status=delivered" },
            { label: "Failed", value: stats.failed, hint: "Could not deliver", color: "red", to: "/admin/orders?status=failed" },
            { label: "Returned", value: stats.returned, hint: "Returned to pharmacy", color: "orange", to: "/admin/orders?status=returned" },
            { label: "Total Open", value: stats.open_orders, hint: "All open (not delivered)", to: "/admin/orders" },
            { label: "Routes", value: stats.total_routes ?? stats.active_routes, hint: "Total routes created", to: "/admin/routes" },
            {
              label: "Copays",
              value: stats.copay_collected_count ?? 0,
              hint: stats.copay_outstanding_count
                ? `${stats.copay_outstanding_count} outstanding · $${(stats.copay_outstanding_total ?? 0).toFixed(2)}`
                : `$${(stats.copay_collected_total ?? 0).toFixed(2)} collected`,
              color: "white",
              to: "/admin/orders?copay=collected",
            },
          ]}
        />
      </section>

      <Card className="mt-8 p-2 rounded-xl border-border bg-card/80 relative" data-testid="overview-map-card">
        {user?.role === "super_admin" && (
          <div className="absolute top-3 right-3 z-[500] flex flex-col items-end gap-2">
            {/* Deliver-type filter pill — super_admin only. Sits top-right
                of the map card above the Freeze-map pill (stacked vertically
                so they share the right rail). */}
            <div
              className="inline-flex items-center rounded-full bg-card/95 border border-border shadow-md p-0.5 text-[11px] font-medium"
              data-testid="map-type-filter"
            >
              {[
                { v: "all", label: "All" },
                { v: "same_day", label: "Same Day" },
                { v: "next_day", label: "Next Day" },
              ].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setMapTypeFilter(o.v)}
                  className={`px-3 py-1 rounded-full transition-colors ${
                    mapTypeFilter === o.v
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid={`map-type-filter-${o.v}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setMapFrozen((v) => !v)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium shadow-md transition-all ${
                mapFrozen
                  ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  : "bg-card text-foreground border border-border hover:bg-secondary"
              }`}
              data-testid="map-freeze-btn"
              title={mapFrozen ? "Unfreeze map (allow pan/zoom)" : "Freeze map (lock current view)"}
            >
              {mapFrozen ? <Lock className="h-3.5 w-3.5" /> : <LockOpen className="h-3.5 w-3.5" />}
              {mapFrozen ? "Map frozen" : "Freeze map"}
            </button>
          </div>
        )}
        <ActiveRoutesMap routes={filteredDetails} driverLocs={driverLocs} pharmacyStops={pharmacyStops} liveDrivers={liveDrivers} unassigned={filteredUnassigned} stopStatusById={stopStatusById} frozen={mapFrozen} />
        {details.length > 0 && (
          <div className="px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-border" data-testid="overview-map-legend">
            {details.map((r) => (
              <span key={r.id} className="inline-flex items-center gap-1.5 text-xs">
                <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: "#d62828" }} />
                <span className="font-medium">{r.label}</span>
                <span className="text-muted-foreground">· {r.driver_name || "—"}</span>
              </span>
            ))}
          </div>
        )}
        <div className="px-3 py-2 flex items-center justify-between text-xs text-muted-foreground gap-3 flex-wrap" data-testid="overview-drivers-legend">
          <div className="flex items-center gap-3 flex-wrap">
            <span><span className="font-mono-num font-semibold text-foreground">{routes.length}</span> active route(s)</span>
            <span className="text-border">·</span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: "#EAB308" }} />
              <span className="font-mono-num font-semibold text-foreground">{unassigned.filter((o) => (o.deliver_type || "").toLowerCase() !== "next_day").length}</span> unassigned <span className="text-muted-foreground">(same day)</span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: UNASSIGNED_NEXT_DAY_HEX }} />
              <span className="font-mono-num font-semibold text-foreground">{unassigned.filter((o) => (o.deliver_type || "").toLowerCase() === "next_day").length}</span> unassigned <span className="text-muted-foreground">(next day)</span>
            </span>
            <span className="text-border">·</span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: "#3a86ff" }} />
              <span className="font-mono-num font-semibold text-foreground">{liveDrivers.filter((d) => d.status === "on_route").length}</span> on route
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: "#234123" }} />
              <span className="font-mono-num font-semibold text-foreground">{liveDrivers.filter((d) => d.status === "online").length}</span> online
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: "#9ca3af" }} />
              <span className="font-mono-num font-semibold text-foreground">{liveDrivers.filter((d) => d.status === "offline").length}</span> offline
            </span>
          </div>
          <Link to={routesHref} className="text-primary hover:underline inline-flex items-center gap-1"><RouteIcon className="h-3 w-3" /> Open Routes Planner</Link>
        </div>

        {/* All online drivers — visible directly on the Overview so admins see who's working. */}
        {(() => {
          const onlineList = (liveDrivers || []).filter((d) => d.status !== "offline");
          if (onlineList.length === 0) {
            return (
              <div className="px-3 py-2.5 border-t border-border text-xs text-muted-foreground" data-testid="overview-online-drivers-empty">
                No drivers online right now.
              </div>
            );
          }
          return (
            <div className="px-3 py-2.5 border-t border-border" data-testid="overview-online-drivers">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2">
                Drivers online ({onlineList.length})
              </div>
              <div className="flex flex-wrap gap-2">
                {onlineList.map((d) => {
                  const onRoute = d.status === "on_route";
                  return (
                    <div
                      key={d.id}
                      className={`inline-flex items-center gap-2 rounded-full pl-1.5 pr-3 py-1 border ${
                        onRoute
                          ? "bg-[#e0f0ff] border-[#3a86ff]/40 text-[#1f4ea3]"
                          : "bg-[#dcfce7] border-[#16A34A]/40 text-[#166534]"
                      }`}
                      title={d.at ? `Last seen ${fmtTime(d.at)}` : "Last seen —"}
                      data-testid={`overview-online-driver-${d.id}`}
                    >
                      <span
                        className={`h-5 w-5 rounded-full grid place-items-center text-[10px] font-bold text-white ${
                          onRoute ? "bg-[#3a86ff]" : "bg-[#16A34A]"
                        }`}
                      >
                        {(d.name || "?").trim().split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase() || "?"}
                      </span>
                      <span className="text-[12px] font-medium leading-tight max-w-[160px] truncate">{d.name || "Unknown"}</span>
                      <span className="text-[9px] uppercase tracking-wider font-semibold opacity-80">
                        {onRoute ? "On Route" : "Online"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </Card>
    </div>
  );
}


// Multi-route map (Leaflet directly via inline JSX with multi-polylines)
import { MapContainer, TileLayer, Marker, Polyline, Popup } from "react-leaflet";
import L from "leaflet";

const pharmacyIcon = (name) => {
  const initials = personInitials(name, "PH");
  return L.divIcon({
    className: "",
    html: `<div style="background:#111;color:#fff;width:28px;height:28px;border-radius:6px;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:10px;letter-spacing:.3px;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3);">${initials}</div>`,
    iconSize: [28, 28], iconAnchor: [14, 14],
  });
};

const PICKUP_COLOR = "#111111"; // black — distinct color for Pick Up orders so dispatch can spot them at a glance

// Human-readable labels + accent colors used inside overview map popups.
const DELIVER_TYPE_LABEL = {
  same_day: "Same Day",
  next_day: "Next Day",
  otc: "OTC",
  pickup: "Pick Up",
};
const PRIORITY_LABEL = {
  normal: "Normal",
  urgent: "Urgent",
  cold_chain: "Cold Chain",
  clinic: "Clinic",
  business: "Business",
};
const PRIORITY_COLOR = {
  normal: "#64748b",     // slate
  urgent: "#dc2626",     // red
  cold_chain: "#0ea5e9", // sky
  clinic: "#8b5cf6",     // purple
  business: "#F59E0B",   // amber
};

const RESTRICTED_FLAG_HTML = `<span
  style="position:absolute;top:-6px;left:-6px;background:#DC2626;color:#fff;font-size:10px;font-weight:900;width:16px;height:16px;border-radius:3px;display:flex;align-items:center;justify-content:center;border:1.5px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.35);line-height:1;"
  title="Order in a Restricted ZIP"
>&#9873;</span>`;

const stopIcon = (initials, color, seq, isPickup = false, isRestricted = false, coSeqs = null) => {
  // "Also stops here" left badge — red pill listing the other sequences
  // that share this exact lat/lng. Prevents dispatch from missing a 2nd
  // delivery at the same door.
  const coBadge = coSeqs && coSeqs.length
    ? `<span style="position:absolute;top:-6px;left:-6px;background:#DC2626;color:#fff;font-size:8px;font-weight:800;min-width:15px;height:15px;padding:0 3px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:1.5px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.35);" title="Also stops #${coSeqs.join(', #')} at this address">${coSeqs.join('/')}</span>`
    : "";
  if (isPickup) {
    return L.divIcon({
      className: "",
      html: `<div style="position:relative;background:${PICKUP_COLOR};color:#fff;width:26px;height:26px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:9px;letter-spacing:.3px;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.55);">${initials}<span style="position:absolute;top:-6px;left:-6px;background:#fff;color:#111;font-size:8px;font-weight:900;width:15px;height:15px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:1.5px solid #111;letter-spacing:0;">P</span>${seq != null ? `<span style="position:absolute;top:-5px;right:-5px;background:#3a86ff;color:#fff;font-size:8px;width:14px;height:14px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:1.5px solid #fff;">${seq}</span>` : ""}</div>`,
      iconSize: [26, 26], iconAnchor: [13, 13],
    });
  }
  return L.divIcon({
    className: "",
    html: `<div style="position:relative;background:${color};color:#fff;width:26px;height:26px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:9px;letter-spacing:.3px;border:2px solid #fff;box-shadow:0 2px 4px rgba(0,0,0,.25);">${initials}${isRestricted ? RESTRICTED_FLAG_HTML : ""}${coBadge}${seq != null ? `<span style="position:absolute;top:-5px;right:-5px;background:#111;color:#fff;font-size:8px;width:14px;height:14px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:1.5px solid #fff;">${seq}</span>` : ""}</div>`,
    iconSize: [26, 26], iconAnchor: [13, 13],
  });
};

const DRIVER_STATUS_COLOR = {
  on_route: "#3a86ff", // sky-blue (matches On Route KPI)
  online:   "#234123", // primary green
  offline:  "#9ca3af", // gray
};

const driverIcon = (status = "on_route") => L.divIcon({
  className: "",
  html: `<div style="position:relative;width:30px;height:30px;"><div style="position:absolute;inset:0;background:${DRIVER_STATUS_COLOR[status]};border-radius:50%;opacity:.25;animation:pulse 1.6s ease-out infinite;"></div><div style="position:absolute;inset:4px;background:${DRIVER_STATUS_COLOR[status]};border-radius:50%;display:flex;align-items:center;justify-content:center;color:#fff;font-size:13px;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3);">🚐</div></div><style>@keyframes pulse{0%{transform:scale(.6);opacity:.5;}100%{transform:scale(1.6);opacity:0;}}</style>`,
  iconSize: [30, 30], iconAnchor: [15, 15],
});

function ActiveRoutesMap({ routes, driverLocs, pharmacyStops, liveDrivers = [], unassigned = [], stopStatusById = {}, frozen = false }) {
  // Center on first available reference: an unassigned order, a route stop, then pharmacy, then fallback to the active workspace center.
  const { active: activeRegion } = useRegion();
  const firstStop = routes[0]?.stops?.[0];
  const center = unassigned[0]
    ? [unassigned[0].lat, unassigned[0].lng]
    : firstStop
      ? [firstStop.lat, firstStop.lng]
      : pharmacyStops[0]
        ? [pharmacyStops[0].lat, pharmacyStops[0].lng]
        : activeRegion.center;
  // In "All regions" mode we ignore the marker-derived center and force the
  // MapContainer to fit the workspace's declared bounds (Leesburg → Homestead).
  // This gives the Super Admin an at-a-glance view of both South & Central FL.
  const useAllBounds = activeRegion?.bounds && activeRegion.id === "all";
  // Drivers whose route is currently rolling OR temporarily paused — both
  // count as "on route" for the black-initials-circle overlay so dispatch
  // still sees the driver's location while paused.
  const onRouteIds = new Set(
    routes
      .filter((r) => r.status === "in_progress" || r.status === "paused")
      .map((r) => r.driver_id)
  );
  const standaloneOnline = (liveDrivers || []).filter(
    (d) => d.status !== "offline" && d.lat != null && d.lng != null && !onRouteIds.has(d.id)
  );
  // driver_id -> vehicle_color (hex from name)
  const vehicleColorByDriver = {};
  (liveDrivers || []).forEach((d) => {
    const hex = vehicleColorHex(d.vehicle_color);
    if (hex) vehicleColorByDriver[d.id] = hex;
  });
  return (
    // In "All regions" mode the container grows taller so Leesburg (top) and
    // Homestead (bottom) both fit vertically without Leaflet zooming out into
    // Louisiana / the Bahamas to satisfy the aspect ratio. Fixed height of
    // 580px and width capped to 80% of the card (centered) per operator
    // spec on 2026-07-25.
    <div
      className="rounded-lg overflow-hidden mx-auto"
      style={{ height: 580, width: "80%" }}
    >
      <MapContainer
        {...(useAllBounds
          ? { bounds: activeRegion.bounds, boundsOptions: { padding: [10, 10], maxZoom: 8 } }
          : { center, zoom: 10 })}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={!frozen}
        dragging={!frozen}
        doubleClickZoom={!frozen}
        touchZoom={!frozen}
        zoomControl={!frozen}
        keyboard={!frozen}
        boxZoom={!frozen}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
        {/* Pharmacy/depot markers are not rendered globally — only the start
            location of each active route is shown (as a black initials circle),
            so dispatch always knows where each polyline begins. Multiple routes
            sharing the same start get a single marker. */}
        {(() => {
          const seen = new Set();
          const startMarkers = [];
          routes.forEach((r) => {
            const s = r.start;
            if (!s || s.lat == null || s.lng == null) return;
            const key = `${s.lat.toFixed(5)},${s.lng.toFixed(5)}`;
            if (seen.has(key)) return;
            seen.add(key);
            startMarkers.push(
              <Marker
                key={`start-${key}`}
                position={[s.lat, s.lng]}
                icon={pharmacyIcon(s.name)}
                zIndexOffset={500}
              >
                <Popup>
                  <div className="font-medium">{s.name || "Start location"}</div>
                  <div className="text-[10px] uppercase tracking-wider mt-1 text-muted-foreground">Route start</div>
                </Popup>
              </Marker>
            );
          });
          return startMarkers;
        })()}
        {routes.map((r, idx) => {
          const polyColor = "#d62828";
          const routeFallbackStatus = r.status === "in_progress" ? "in_transit" : "assigned";
          return (
            <React.Fragment key={r.id}>
              {r.polyline?.length > 1 && (
                <Polyline positions={r.polyline} pathOptions={{ color: polyColor, weight: 5, opacity: 0.9 }} />
              )}
              {r.stops?.map((s) => {
                const isPickup = (s.deliver_type || "").toLowerCase() === "pickup" || !!s.is_pickup;
                // Restricted-ZIP red flag: only while the order is on hold /
                // awaiting Super Admin approval. Rejected orders are hard-deleted
                // so they never appear on the map; approved orders drop the flag.
                const isRestricted = !!(s.restricted_zip || s.restricted) && (s.approval_status !== "approved");
                // Any OTHER sequence numbers sharing this exact lat/lng — a
                // red "N/M" pill on the LEFT of the marker so dispatch and
                // drivers instantly see 2+ stops share the same door.
                const groupKey = (s.lat != null && s.lng != null)
                  ? `${s.lat.toFixed(5)},${s.lng.toFixed(5)}` : null;
                const coSeqs = groupKey
                  ? (r.stops || [])
                      .filter((o) => o.lat != null && o.lng != null
                        && `${o.lat.toFixed(5)},${o.lng.toFixed(5)}` === groupKey
                        && o.sequence !== s.sequence)
                      .map((o) => o.sequence)
                  : [];
                // Use the stop's CURRENT order status when available so a delivered
                // stop turns green immediately on the next refresh — independent of
                // the route's overall status. Falls back to the route-level status
                // for stops whose order isn't in the cache yet.
                const liveStatus = stopStatusById[s.order_id] || routeFallbackStatus;
                const stopKey =
                  liveStatus === "delivered" ? "delivered" :
                  liveStatus === "failed" ? "failed" :
                  liveStatus === "returned" ? "returned" :
                  liveStatus === "in_transit" ? "in_transit" :
                  "assigned"; // assigned / pending fallback
                const baseColor = STATUS_HEX[stopKey] || STATUS_HEX.assigned;
                const markerColor = isPickup ? PICKUP_COLOR : baseColor;
                return (
                  <Marker
                    key={`${r.id}-${s.order_id}`}
                    position={[s.lat, s.lng]}
                    icon={stopIcon(personInitials(s.customer_name), markerColor, s.sequence, isPickup, isRestricted, coSeqs)}
                  >
                    <Popup>
                      {/* Single header line above patient name — always
                          three fields (Pharmacy Nickname · Deliver Type ·
                          Priority) so the shape is consistent no matter
                          which order the operator clicks on. Missing
                          deliver_type falls back to "Same Day", missing
                          priority falls back to "Normal". Uses an explicit
                          near-black text color instead of `text-foreground`
                          because the Leaflet popup always has a white body,
                          so the theme's dark foreground would go invisible
                          on the popup in dark mode. */}
                      <div className="text-[11px] font-semibold text-black mb-1" data-testid={`overview-stop-header-${s.order_id}`}>
                        {[
                          s.pharmacy_nickname || s.pharmacy_name || "Pharmacy",
                          DELIVER_TYPE_LABEL[s.deliver_type] || (s.deliver_type ? s.deliver_type : "Same Day"),
                          PRIORITY_LABEL[s.priority] || (s.priority ? s.priority : "Normal"),
                        ].join(" - ")}
                      </div>
                      <div className="font-semibold text-black uppercase tracking-wide" data-testid={`overview-stop-name-${s.order_id}`}>
                        {s.customer_name}
                      </div>
                      {s.address && (
                        <div className="text-xs text-black/80 mt-0.5" data-testid={`overview-stop-address-${s.order_id}`}>
                          {s.address}
                        </div>
                      )}
                      {s.phone && (
                        <div className="text-xs text-black/80" data-testid={`overview-stop-phone-${s.order_id}`}>
                          {s.phone}
                        </div>
                      )}
                      <div className="text-[11px] font-bold text-black/70 mt-1 uppercase tracking-wider" data-testid={`overview-stop-route-${s.order_id}`}>
                        ON ROUTE: {r.label} · stop {s.sequence}
                      </div>
                      {coSeqs.length > 0 && (
                        <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "#DC2626", color: "#fff" }} data-testid={`overview-stop-cosequences-${s.order_id}`}>
                          Also stop{coSeqs.length > 1 ? "s" : ""} #{coSeqs.join(", #")} here
                        </div>
                      )}
                      {isPickup && (
                        <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "#111", color: "#fff" }}>
                          Pick Up
                        </div>
                      )}
                      <div className="text-[10px] uppercase tracking-wider mt-1" style={{ color: markerColor }}>{isPickup ? "PICK UP" : (STATUS_LABEL[stopKey] || stopKey.toUpperCase())}</div>
                    </Popup>
                  </Marker>
                );
              })}
              {driverLocs[r.driver_id] && (
                <Marker position={[driverLocs[r.driver_id].lat, driverLocs[r.driver_id].lng]} icon={driverIcon("on_route")}>
                  <Popup>
                    <div className="font-medium">{r.driver_name}</div>
                    <div className="text-[10px] uppercase tracking-wider mt-1" style={{ color: DRIVER_STATUS_COLOR.on_route }}>ON ROUTE</div>
                    <div className="text-xs">Last seen {fmtTime(driverLocs[r.driver_id].at)}</div>
                  </Popup>
                </Marker>
              )}
            </React.Fragment>
          );
        })}
        {/* Unassigned + Accepted orders — yellow pins for never-touched, light blue for received-from-pharmacy (Accepted). Purple if Pick Up. Helps dispatch see what still needs routing. */}
        {unassigned.map((o) => {
          const isPickup = (o.deliver_type || "").toLowerCase() === "pickup";
          // Restricted-ZIP flag on unassigned/on-hold orders — disappears once approved.
          const isRestricted = !!(o.restricted_zip || o.restricted) && (o.approval_status !== "approved");
          // Previously-failed orders (failed_count > 0) stay red in the
          // unassigned pool so dispatch immediately sees retry candidates.
          const prevFailed = (o.failed_count || 0) > 0 && o.status !== "delivered";
          const st = effectiveStatus(o);
          const color = prevFailed
            ? STATUS_HEX.failed
            : (isPickup ? PICKUP_COLOR : markerColor(o, st));
          const failedTwice = (o.failed_count || 0) >= 2;
          return (
            <Marker
              key={`ua-${o.id}`}
              position={[o.lat, o.lng]}
              icon={stopIcon(personInitials(o.customer_name), color, null, isPickup, isRestricted)}
            >
              <Popup>
                {/* Same header line format as route stops — always
                    Pharmacy Nickname - Deliver Type - Priority. */}
                <div className="text-[11px] font-semibold text-black mb-1" data-testid={`overview-unassigned-header-${o.id}`}>
                  {[
                    o.pharmacy_nickname || o.pharmacy_name || "Pharmacy",
                    DELIVER_TYPE_LABEL[o.deliver_type] || (o.deliver_type ? o.deliver_type : "Same Day"),
                    PRIORITY_LABEL[o.priority] || (o.priority ? o.priority : "Normal"),
                  ].join(" - ")}
                </div>
                <div className="font-medium">{o.customer_name}</div>
                <div className="text-xs text-muted-foreground">{o.address}</div>
                {isPickup && (
                  <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "#ede4ff", color: "#5B21B6" }}>
                    Pick Up
                  </div>
                )}
                {prevFailed && (
                  <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "#fee2e2", color: "#b91c1c" }}>
                    {failedTwice ? "Failed 2× — return to pharmacy" : "Previously failed — retry"}
                  </div>
                )}
                <div className="text-[10px] uppercase tracking-wider mt-1" style={{ color }}>
                  {isPickup ? "PICK UP" : (prevFailed ? "FAILED" : (STATUS_LABEL[st] || "UNASSIGNED"))}
                </div>
              </Popup>
            </Marker>
          );
        })}
        {standaloneOnline.map((d) => (
          <Marker key={`live-${d.id}`} position={[d.lat, d.lng]} icon={driverIcon("online")}>
            <Popup>
              <div className="font-medium">{d.name}</div>
              <div className="text-[10px] uppercase tracking-wider mt-1" style={{ color: DRIVER_STATUS_COLOR.online }}>ONLINE</div>
              <div className="text-xs">Last seen {fmtTime(d.at)}</div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
